import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Star, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./motion";

const CARD_META = {
  "luna-the-unicorn": { label: "The Dreamer", desc: "For little imaginations that are always dreaming somewhere magical.", pill: "#EEE8FF", glow: "rgba(245,140,200,0.35)" },
  "freddy-the-frog": { label: "The Calm One", desc: "For little ones who love quiet moments, cozy routines and gentle adventures.", pill: "#E4F6E9", glow: "rgba(126,212,145,0.35)" },
  "honey-the-bear": { label: "The Protector", desc: "For kids who want a big, familiar hug waiting beside them at night.", pill: "#FFF3D1", glow: "rgba(255,216,107,0.4)" },
  "gigi-the-giraffe": { label: "The Adventurer", desc: "For curious little explorers who are always wondering what comes next.", pill: "#FFE8D6", glow: "rgba(255,190,140,0.4)" },
  "poppy-the-pig": { label: "The Brave One", desc: "For big feelings, brave little hearts and kids learning how strong they can be.", pill: "#FBE7EF", glow: "rgba(245,140,200,0.35)" },
  "hugo-the-hippo": { label: "The Wisher", desc: "For gentle dreamers who believe wishes can lead to amazing things.", pill: "#EEE8FF", glow: "rgba(200,181,255,0.4)" },
  "ziggy-the-zebra": { label: "The Daydreamer", desc: "Every stripe hides a story.", pill: "#DFF3FF", glow: "rgba(158,220,255,0.4)" },
  "rio-the-macaw": { label: "The Storyteller", desc: "I've got a hundred tales to tell.", pill: "#FFE8D6", glow: "rgba(255,150,120,0.35)" },
  "wooly-the-sheep": { label: "The Sleepyhead", desc: "Count me in. Literally.", pill: "#FBE7EF", glow: "rgba(245,140,200,0.3)" },
  "scout-the-horse": { label: "The Brave One", desc: "Giddy-up, dreamland awaits.", pill: "#FFF3D1", glow: "rgba(255,216,107,0.4)" },
  "ellie-the-elephant": { label: "The Rememberer", desc: "I'll never forget our adventures.", pill: "#EEE8FF", glow: "rgba(200,181,255,0.4)" },
  "nova-the-narwhal": { label: "The Stargazer", desc: "The ocean of stars is calling.", pill: "#DFF3FF", glow: "rgba(158,220,255,0.45)" },
};

export default function FindBuddySection({ buddies }) {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [dot, setDot] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setDot(Math.round(el.scrollLeft / (el.scrollWidth - el.clientWidth || 1) * (buddies.length - 1)));
  };

  return (
    <section className="relative overflow-hidden px-6 py-20 lg:py-28" data-testid="find-buddy-section">
      {/* edge doodles */}
      <Moon className="absolute left-8 top-16 h-16 w-16 rotate-[-12deg] text-moon/40" fill="rgba(255,216,107,0.25)" aria-hidden="true" />
      <Star className="absolute left-16 top-48 h-5 w-5 text-pink" fill="#F58CC8" aria-hidden="true" />
      <Star className="absolute right-14 top-24 h-7 w-7 text-moon" fill="#FFD86B" aria-hidden="true" />
      <Star className="absolute right-8 top-56 h-4 w-4 text-pink" fill="#F58CC8" aria-hidden="true" />
      <Moon className="absolute right-16 bottom-24 h-10 w-10 rotate-[18deg] text-lavdeep/50" aria-hidden="true" />
      <Star className="absolute left-10 bottom-16 h-6 w-6 text-moon/70" fill="rgba(255,216,107,0.5)" aria-hidden="true" />
      <p className="absolute -left-2 top-1/3 hidden -rotate-90 font-script text-2xl text-lavdeep/60 xl:block" aria-hidden="true">A Brighter Bedtime Together ♡</p>
      <p className="absolute -right-4 top-1/4 hidden rotate-90 font-script text-2xl text-lavdeep/60 xl:block" aria-hidden="true">Big Dreams Brighter Tomorrows ♡</p>

      <Reveal className="relative mx-auto max-w-3xl text-center">
        <p className="text-xs font-bold uppercase tracking-[0.35em] text-pinkdeep">— Meet Your Match —</p>
        <h2 className="mt-5 font-display text-4xl font-medium leading-[1.02] text-navy sm:text-5xl lg:text-6xl">Find Their Buddy</h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-navy/60">
          Every little dreamer is different. Meet the Buddies and discover the friend that feels just right for them.
        </p>
      </Reveal>

      <div className="relative mx-auto mt-14 max-w-7xl">
        <button onClick={() => trackRef.current?.scrollBy({ left: -trackRef.current.clientWidth * 0.6, behavior: "smooth" })}
          aria-label="Scroll Buddies left" data-testid="find-arrow-left"
          className="absolute -left-5 top-[38%] z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-[0_8px_30px_rgba(16,42,86,0.15)] transition hover:scale-110 hover:bg-pink xl:flex">
          <ChevronLeft size={22} />
        </button>
        <button onClick={() => trackRef.current?.scrollBy({ left: trackRef.current.clientWidth * 0.6, behavior: "smooth" })}
          aria-label="Scroll Buddies right" data-testid="find-arrow-right"
          className="absolute -right-5 top-[38%] z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-[0_8px_30px_rgba(16,42,86,0.15)] transition hover:scale-110 hover:bg-pink xl:flex">
          <ChevronRight size={22} />
        </button>
      <div ref={trackRef} onScroll={onScroll}
        className="relative flex gap-5 overflow-x-auto pb-4 no-scrollbar snap-x snap-mandatory"
        data-testid="find-buddy-grid">
        {buddies.map((b, i) => {
          const meta = CARD_META[b.slug] || { label: b.personality, desc: b.short_desc, pill: "#FBE7EF", glow: "rgba(245,140,200,0.3)" };
          return (
            <button
              key={b.slug}
              onClick={() => navigate(`/buddy/${b.slug}`)}
              className="group w-[82vw] max-w-96 shrink-0 snap-center text-left transition-transform duration-300 hover:-translate-y-2 sm:w-[46vw] lg:w-[calc(33.333%-14px)] lg:max-w-none xl:w-[calc(25%-15px)]"
              data-testid={`find-card-${b.slug}`}
            >
              <div className="overflow-hidden rounded-[1.75rem] bg-blush shadow-[0_12px_40px_rgba(16,42,86,0.08)]">
                <img src={b.image} alt={`${b.name} the ${b.animal}`} loading="lazy"
                  className="aspect-[2/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
              </div>
              <div className="px-2 pb-2 pt-4 text-center">
                <span className="inline-block rounded-full px-3 py-1 font-body text-[10px] font-extrabold uppercase tracking-[0.18em] text-navy/70" style={{ background: meta.pill }}>
                  {meta.label}
                </span>
                <p className="mt-2.5 font-display text-2xl font-medium text-navy">{b.name}</p>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-navy/45">The {b.animal}</p>
                <p className="mx-auto mt-3 min-h-16 text-[13px] leading-relaxed text-navy/55">{meta.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-pink px-5 py-2.5 font-body text-xs font-bold text-navy transition group-hover:bg-moon">
                  Meet {b.name} <ArrowRight size={13} />
                </span>
              </div>
            </button>
          );
        })}
      </div>
      </div>
      <div className="mt-4 flex justify-center gap-2" data-testid="find-buddy-dots">
        {buddies.map((b, i) => (
          <span key={b.slug} className={`h-2 rounded-full transition-all ${dot === i ? "w-5 bg-pink" : "w-2 bg-blush"}`} />
        ))}
      </div>

    </section>
  );
}
